import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';

import type { AssetResponse, BulkResultResponse } from '../../api';
import { listAssets, updateAssets } from '../../api';
import { specOf } from '../../constants/assetTypes';
import { useAssetLibraryPermissions } from '../../useAssetLibraryPermissions';
import { useVocabulary } from '../../vocabulary/useVocabulary';
import { conflictingInheritedFields, conflictLabels } from './sourceImageInheritance';
import { SourceImageSelect, useSourceImageOptions } from './SourceImagePicker';

/**
 * Source image controls for the current selection.
 *
 * Split out of `AssetMetadataForm` rather than folded into its field loop.
 * Linking is a normal PATCH, but it needs a picker of existing source images
 * rather than a text box, so it lives here next to the thing it belongs with.
 * Whether an asset *is* a source image is fixed at upload: a source image shows
 * its code here, and a mis-classified upload is deleted and uploaded again.
 *
 * The picker saves immediately rather than joining the drawer's Save button.
 *
 * Linking also copies the source image's campaign and rights metadata down onto
 * the assets cut from it — the hero is the source of truth for the shoot it came
 * from. That copy overwrites, so a link that would replace values somebody
 * already filled in asks first, in a modal. The copy is opt-in server-side
 * (`inherit_metadata`), and this component is the only thing that sets it: the
 * flag is how the user's answer reaches the server, rather than the server
 * assuming consent from the presence of a pointer.
 *
 * The modal is deliberate. This started as an inline amber panel under the
 * picker, which was easy to miss: the eye is on the dropdown that was just
 * used, the panel opens below the fold of a narrow drawer, and the rest of
 * this section is already amber for the "required" hint — so the one message
 * that needs an answer looked like the two that do not. Losing hand-typed
 * metadata is not recoverable by undo, so it gets the interruption.
 *
 * Amber stays, but only as the accent inside the dialog. It is this page's
 * colour for "look at this before you continue" — the upload modal's rejected
 * files, the missing-metadata warnings on the cards — and the point of moving
 * the message was that amber alone was not enough to carry it.
 */

interface SourceImageSectionProps {
  selectedAssets: AssetResponse[];
  readOnly?: boolean;
}

/** Surface `skipped` — a partial success reported as a success is a lie. */
function report(result: BulkResultResponse, verb: string) {
  if (result.skipped.length > 0) {
    toast.error(
      `${verb} ${result.updated.length}, skipped ${result.skipped.length}: ${result.skipped[0].reason}`,
    );
  } else {
    toast.success(
      `${verb} ${result.updated.length} asset${result.updated.length === 1 ? '' : 's'}`,
    );
  }
}

export function SourceImageSection({
  selectedAssets,
  readOnly: readOnlyProp = false,
}: SourceImageSectionProps) {
  const queryClient = useQueryClient();
  // A carousel's links are derived from its frames, and the backend refuses an
  // edit to them, so the section shows them without offering one.
  const readOnly = readOnlyProp || selectedAssets.some((asset) => specOf(asset).hasFrames);
  const assetIds = selectedAssets.map((asset) => asset.id);

  // A selection can straddle both kinds. Rather than guess, the controls only
  // offer the action that is unambiguous for every selected row.
  const allSourceImages =
    selectedAssets.length > 0 && selectedAssets.every((a) => a.asset_type === 'source_image');
  const noneSourceImages =
    selectedAssets.length > 0 && selectedAssets.every((a) => a.asset_type !== 'source_image');
  const isMixed = !allSourceImages && !noneSourceImages;
  // Whether the picker applies is the registry's call, the same flag the
  // publish gate reads in `needsSourceImage`: a kind that cannot have sources
  // (a film is not cut from one) gets no picker. The backend refuses the same
  // request, so hiding the control keeps the drawer from offering a 422.
  const cannotHaveSources = selectedAssets.some((a) => !specOf(a).canHaveSources);

  // The links the whole selection already agrees on. Compared as an ordered
  // list, not a set: position is what orders the thumbnail stack, so two assets
  // carrying the same sources in a different order do not share a link and the
  // picker must not claim they do.
  // `?? []` throughout rather than trusting the type: this is server data, and
  // a response predating the join table — an older backend, a cached page —
  // omits the field entirely. Reading `.join` off that throws during render,
  // which unmounts the whole metadata drawer rather than just this one control.
  // "No links recorded" is both true of such a response and survivable.
  const sourcesOf = (asset: AssetResponse) => asset.source_image_ids ?? [];
  const sharedSources =
    noneSourceImages &&
    selectedAssets.every((a) => sourcesOf(a).join(',') === sourcesOf(selectedAssets[0]).join(','))
      ? sourcesOf(selectedAssets[0])
      : [];

  // Not fetched at all for roles that cannot see source images: the backend
  // refuses a source-image-only listing to them, and they get the codes below
  // instead of a picker. Emptied as well as not fetched: disabling the query
  // does not drop a list cached earlier in the session under a role that could
  // see it.
  //
  // `sharedSources` so an existing link to a staged source still shows as
  // linked, rather than the asset reading as unlinked.
  const { permissions } = useAssetLibraryPermissions();
  // Region members, for intersecting the sources' country rights.
  const { vocabulary } = useVocabulary();
  const { options: fetchedSourceImageOptions } = useSourceImageOptions(
    noneSourceImages && !cannotHaveSources && permissions.can_view_source_images,
    sharedSources,
  );
  const sourceImageOptions = permissions.can_view_source_images ? fetchedSourceImageOptions : [];
  // What a read-only view without the picker's options can still say about
  // the link: the codes ride on the asset itself.
  const sharedSourceCodes =
    sharedSources.length > 0 ? (selectedAssets[0].source_image_codes ?? []) : [];

  // A link the user has chosen but not yet confirmed, because going ahead
  // would overwrite metadata already on the selected assets. Held here rather
  // than applied optimistically: until Replace is pressed, nothing has changed.
  const [pending, setPending] = useState<{
    ids: string[];
    /**
     * The sources' codes, so the question can name what is about to be copied.
     * A list because several can be picked, and "these will overwrite your
     * metadata" without saying which sources is not a question anyone can
     * answer.
     */
    codes: string[];
    fields: string[];
  } | null>(null);

  // The freshness check that runs before the conflict comparison. Its own flag
  // rather than a mutation, because it must lock the picker: it is a network
  // round trip during which the control would otherwise still accept input.
  const [checking, setChecking] = useState(false);

  // Picks not yet saved, or `null` for "showing what is stored". A composite is
  // assembled one tick at a time and each intermediate state is a set the user
  // never meant to commit, so the picker edits this and the Save button is what
  // reaches the server.
  //
  // `null` rather than seeding from `sharedSources`: the stored links change
  // underneath this component whenever the list query refetches, and a draft
  // initialised from them could not tell "the user cleared everything" from
  // "nothing is linked yet".
  const [draft, setDraft] = useState<string[] | null>(null);
  const dirty = draft !== null && draft.join(',') !== sharedSources.join(',');

  // Both dropped when the selection changes. The warning names fields on the
  // assets that were selected when it was raised, and answering "Replace"
  // afterwards would apply it to a different set than the one it described.
  // The draft goes for the same reason: picks made against one selection are
  // not an intention about another.
  const selectionKey = assetIds.join(',');
  useEffect(() => {
    setPending(null);
    setDraft(null);
  }, [selectionKey]);

  // Focus moves into the dialog and back out again. `aria-modal` is a claim
  // about where focus is, not a mechanism: without this the still-focused
  // `<select>` behind the backdrop keeps receiving keystrokes, and an arrow
  // key there picks a different source image while the question about the first
  // one is still on screen. Restoring on close puts the caret back on the
  // control the user was using.
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!pending) return;
    const previous = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => previous?.focus?.();
  }, [pending]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['asset-library', 'assets'] });
    void queryClient.invalidateQueries({ queryKey: ['asset-library', 'key-visuals'] });
    // Singular: the permalink fallback in `AssetLibraryPage` fetches an asset
    // that is not in the current page under its own key. Query keys match
    // element-wise, so `'assets'` does not reach `'asset'` — and without this
    // a link made from a permalink-opened drawer leaves every field on screen
    // showing its pre-link value, which reads as a save that failed.
    void queryClient.invalidateQueries({ queryKey: ['asset-library', 'asset'] });
  };

  const linkMutation = useMutation({
    mutationFn: (sourceImageIds: string[]) =>
      updateAssets({
        asset_ids: assetIds,
        // The whole set, every time. The server replaces rather than appends,
        // which is the only way an unpick can be expressed at all — so an empty
        // list is "unlink", and it is how a bad link gets cleared.
        fields: { source_image_ids: sourceImageIds },
        // The copy is opt-in server-side, and this call site is the one that
        // has the user's answer: either nothing was going to be overwritten,
        // or they pressed Replace. Unlinking has nothing to inherit from.
        inherit_metadata: sourceImageIds.length > 0,
      }),
    onSuccess: (result, sourceImageIds) => {
      report(result, sourceImageIds.length === 0 ? 'Unlinked' : 'Linked');
      setPending(null);
      // Back to showing what is stored. The refetch below is what the picker
      // then reflects — keeping the draft would leave it showing the user's
      // input over the top of a save that may have skipped some assets.
      setDraft(null);
      invalidate();
    },
    // The server's reason, when it gives one. A refused link names the source
    // image at fault — which may be one the picker no longer shows, such as a
    // staged source linked before only published ones could be — and without
    // it the user is told nothing they can act on.
    onError: (error) => {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data
        ?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not link to source image');
    },
  });

  // Every way out of the dialog goes through here. Once Replace has been
  // pressed the PATCH is in flight and cannot be recalled, so dismissing would
  // close the question while the answer it asked about is still being applied
  // — the overwrite lands anyway and the only thing the user sees is a success
  // toast for something they just cancelled. Ignoring the dismissal is the
  // honest behaviour, and it matches the upload modal, which refuses the same
  // click while bytes are moving.
  const dismiss = () => {
    if (linkMutation.isPending) return;
    // The picks survive the cancellation. Assembling three sources is work, and
    // the answer to "replace your metadata?" is "no, not yet" far more often
    // than it is "no, and throw away what I just chose" — so the draft stays
    // and the Save button comes back.
    setDraft(pending?.ids ?? null);
    setPending(null);
  };

  // Escape cancels, as it should for any modal — but it has to be taken in the
  // capture phase and stopped there. The detail overlay listens for Escape on
  // `window` and closes itself unconditionally, so without this, dismissing
  // this question would also throw away the overlay it was asked in. Capturing
  // at the window runs before that bubble-phase listener; stopping propagation
  // there means it never sees the key at all. The key is still swallowed while
  // the link is in flight — the overlay must not close then either.
  useEffect(() => {
    if (!pending) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      dismiss();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, linkMutation.isPending]);

  const handlePick = async (sourceImageIds: string[]) => {
    setPending(null);
    // Unlinking copies nothing, so there is never anything to warn about.
    if (sourceImageIds.length === 0) {
      linkMutation.mutate([]);
      return;
    }

    // Re-read *both* sides before comparing, rather than trusting what is on
    // screen. Every cached copy here can be up to the app's five-minute
    // `staleTime` old:
    //
    // - The picker list is cached under its own key and nothing outside this
    //   component invalidates it, so editing a hero and then linking a
    //   derivative would compare against the hero's pre-edit values.
    // - `selectedAssets` comes from the list query, which is invalidated by
    //   this page's own saves but not by anybody else's. Those are the rows
    //   about to be overwritten — the side where being wrong costs data — so
    //   guessing at their contents is the worse of the two.
    //
    // Either way the failure is the same: the values happen to agree, no
    // conflict is found, no question is asked, and the server overwrites. A
    // warning computed from a stale cache is not a safeguard.
    //
    // Two requests, not one per asset: `ids` fetches the whole selection in
    // one call and the server sizes the page to it. The sources go the same
    // way — what lands depends on all of them together, so re-reading some and
    // guessing at the rest would resolve to a value neither side ever holds.
    // A colleague's edit in the moments between this read and the PATCH can
    // still slip through — only a transaction could close that, and it is not
    // what the warning is for.
    let sourceImages: AssetResponse[];
    let current: AssetResponse[] = selectedAssets;
    // Whether the comparison below is running on values just read from the
    // server. When it is not, the answer is a guess — and a guess is a reason
    // to ask rather than a reason to go ahead.
    let checked = false;
    setChecking(true);
    try {
      const [heroes, fresh] = await Promise.all([
        listAssets({ ids: sourceImageIds.join(',') }),
        listAssets({ ids: assetIds.join(',') }),
      ]);
      // Back into pick order. The list endpoint makes no ordering promise, and
      // order is meaningful here — it is the order the links are written in.
      sourceImages = sourceImageIds
        .map((id) => heroes.assets.find((asset) => asset.id === id))
        .filter((asset): asset is AssetResponse => asset !== undefined);
      // A source deleted since the picker was listed drops out here, and the
      // PATCH will refuse it by name. Better to warn from what is really there
      // than from what was cached.
      checked = sourceImages.length === sourceImageIds.length;
      // Rows deleted since the page was listed simply drop out: they are not
      // ours to warn about, and the PATCH reports them as skipped.
      current = fresh.assets;
    } catch {
      // Unknown state, so fall back to the cached heroes purely to name them in
      // the question. An empty list is what drives the dialog's "could not be
      // checked" wording.
      sourceImages = sourceImageIds
        .map((id) => sourceImageOptions.find((option) => option.id === id))
        .filter((option): option is AssetResponse => option !== undefined);
    } finally {
      setChecking(false);
    }

    const conflicts = checked ? conflictingInheritedFields(current, sourceImages, vocabulary) : [];
    if (checked && conflicts.length === 0) {
      linkMutation.mutate(sourceImageIds);
      return;
    }
    setPending({
      ids: sourceImageIds,
      codes: sourceImages
        .map((source) => source.source_image_code)
        .filter((code): code is string => Boolean(code)),
      fields: conflictLabels(conflicts),
    });
  };

  if (selectedAssets.length === 0) return null;

  // Waiting on an answer counts as busy: the picker behind the dialog must not
  // accept another choice while the question about the last one is unanswered.
  const busy = linkMutation.isPending || checking || pending !== null;

  return (
    <div className="flex flex-col gap-2 border-t border-slate-200 pt-3">
      <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
        Source image
      </span>

      {isMixed ? (
        // A source image cannot be linked, so no one picker applies to every
        // selected row; the mixed case refuses to act.
        <p className="text-[11.5px] text-amber-600">
          Selection mixes source images with assets derived from one. Select only derived assets to
          link a source image.
        </p>
      ) : (
        <>
          {allSourceImages && (
            <p className="text-[11.5px] text-slate-500">
              {selectedAssets.length === 1
                ? (selectedAssets[0].source_image_code ?? 'Code pending')
                : `${selectedAssets.length} source images`}
            </p>
          )}

          {noneSourceImages && cannotHaveSources && (
            <p className="text-[11px] text-slate-400">
              Film assets have no source image — they aren’t cut from a still.
            </p>
          )}

          {noneSourceImages && cannotHaveSources && sharedSources.length > 0 && !readOnly && (
            // A film should not have a link, but some do: rows written before
            // the rule existed. Hiding the picker from films hides the only
            // control that could undo them, leaving exactly the assets the rule
            // protects against stranded — so the clear stays, on its own, while
            // the offer to link does not. The backend draws the same line: it
            // refuses setting a link on a film and permits clearing one.
            <div className="flex flex-col items-start gap-1">
              <p className="text-[11.5px] text-amber-700">
                This film is linked to a source image. Films aren’t cut from a still, so the link
                means nothing.
              </p>
              <button
                type="button"
                disabled={busy}
                onClick={() => void handlePick([])}
                className="rounded border border-amber-300 px-2 py-1 text-[11.5px] text-amber-700 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Remove link
              </button>
            </div>
          )}

          {noneSourceImages && !cannotHaveSources && (
            <label className="flex flex-col gap-1">
              <span className="text-[11px] text-slate-500">
                Source images
                {sharedSources.length === 0 && !readOnly && (
                  <span className="ml-1 text-amber-500">*</span>
                )}
              </span>
              {sourceImageOptions.length === 0 ? (
                readOnly ? (
                  sharedSourceCodes.length > 0 ? (
                    <p className="text-[12px] text-slate-700">{sharedSourceCodes.join(', ')}</p>
                  ) : (
                    <p className="text-[12px] text-slate-400">—</p>
                  )
                ) : (
                  // An empty picker with no explanation is a dead end: the user
                  // is told a link is required and given nothing to link to. Say
                  // what to do instead.
                  <p className="rounded-md border border-amber-200 bg-amber-50/40 px-2 py-1.5 text-[11.5px] text-amber-700">
                    No published source images yet. Upload the hero image for this campaign as a
                    Source image and publish it — it can then be picked here.
                  </p>
                )
              ) : (
                <>
                  <SourceImageSelect
                    // The pending choice shows in the control while its warning
                    // is on screen — the question is about the options the user
                    // just picked, so the picker has to keep showing them.
                    value={pending?.ids ?? draft ?? sharedSources}
                    options={sourceImageOptions}
                    disabled={busy || readOnly}
                    required={!readOnly}
                    emptyLabel={sharedSources.length === 0 ? 'Not linked' : '—'}
                    onChange={setDraft}
                  />
                  {/* Only once there is something to save. Ticking used to save
                      on the spot, which was right for a control that took one
                      answer — but a composite is assembled tick by tick, and
                      saving each one would fire a PATCH and an overwrite
                      question per source, three times over, for a selection the
                      user had not finished making. So the picks are a draft and
                      this commits them. */}
                  {dirty && !readOnly && (
                    <span className="flex items-center gap-2 pt-0.5">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void handlePick(draft ?? [])}
                        className="rounded-lg bg-[#0014cc] px-2.5 py-1 text-[11.5px] font-medium text-white transition-colors hover:bg-[#0010a3] disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                      >
                        {linkMutation.isPending ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setDraft(null)}
                        className="rounded-lg px-2 py-1 text-[11.5px] text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-300"
                      >
                        {/* "Discard", not "Cancel". The overwrite dialog this
                            can open has a Cancel of its own, and two buttons a
                            few pixels apart with the same word cancelling
                            different things is a choice nobody should have to
                            read twice. */}
                        Discard
                      </button>
                      <span className="text-[11px] text-slate-400">
                        {draft?.length === 0
                          ? 'Will unlink'
                          : `${draft?.length} source image${draft?.length === 1 ? '' : 's'}`}
                      </span>
                    </span>
                  )}
                </>
              )}
              {sharedSources.length === 0 && !dirty && !readOnly && !pending && (
                <span className="text-[11px] text-amber-600">
                  Required — these assets can’t be published without it.
                </span>
              )}
            </label>
          )}
        </>
      )}

      {/* Portalled to `body` for the same reason the detail overlay is: the
          page renders inside `<main class="relative z-[1]">`, which is a
          stacking context, so a `fixed inset-0 z-[100]` rendered in place is
          resolved *within* it and comes out underneath the app's sidebar.
          Portalling also puts this after the detail overlay's own portal in
          the DOM, so it wins on equal z-index when the drawer is opened from
          there. */}
      {pending &&
        createPortal(
          // Chrome copied from `UploadBatchModal`, the other modal on this
          // page, down to the type sizes: same backdrop, same white panel,
          // same header rule, same footer with the Ford blue primary on the
          // right. The shared `components/ConfirmModal` would have been less
          // code, but it is built for the rest of Content Tracker — emoji
          // icons, `blue-600`, 14px body copy — and next to the Asset
          // Library's 12.5px slate it reads as a different product.
          <>
            {/* Above `z-[100]`, not at it. The upload modal's `z-[90]/z-[100]`
                pair works because it sits over the page; this one only ever
                opens inside the detail overlay, whose own dialog is `z-[100]`
                — so a backdrop at 90 was painted over every single time and
                the dim never appeared at all. */}
            <div className="fixed inset-0 z-[110] bg-black/50" aria-hidden="true" />
            <div
              className="fixed inset-0 z-[120] flex items-center justify-center p-4"
              onClick={(event) => {
                // Backdrop dismisses, like the upload modal. Cancelling is the
                // safe answer here, so an accidental click outside costs a
                // re-pick rather than the metadata.
                if (event.target === event.currentTarget) dismiss();
              }}
            >
              <div
                ref={dialogRef}
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-labelledby="key-visual-overwrite-title"
                className="w-full max-w-md rounded-lg bg-white shadow-xl outline-none"
              >
                <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" strokeWidth={2} />
                  <h2
                    id="key-visual-overwrite-title"
                    className="text-[14px] font-bold text-slate-800"
                  >
                    Replace existing metadata?
                  </h2>
                </div>

                <div className="space-y-2.5 px-4 py-3">
                  <p className="text-[12.5px] text-slate-600">
                    Linking copies{' '}
                    <span className="font-medium text-slate-800">
                      {pending.codes.length > 0 ? pending.codes.join(', ') : 'this source image'}
                    </span>
                    ’s metadata onto{' '}
                    {selectedAssets.length === 1
                      ? 'this asset'
                      : `these ${selectedAssets.length} assets`}
                    .
                  </p>
                  {/* Said only when it applies. With several sources what lands
                      is not any one of them — the earliest expiry, the strongest
                      AI indicator, nothing where they disagree — and a user who
                      assumes the first one wins would read the field list below
                      as the wrong promise. */}
                  {pending.ids.length > 1 && (
                    <p className="text-[11.5px] text-slate-500">
                      Where these disagree, the safest value is used: the earliest expiry, the
                      strongest AI indicator, and nothing at all for the rest.
                    </p>
                  )}
                  {/* The fields are the whole point of asking — "this will
                      overwrite metadata" gives no basis for an answer — so they
                      are called out rather than run into the sentence above.
                      Amber panel, matching the one the upload modal uses for
                      rejected files. */}
                  <div className="rounded-md border border-amber-200 bg-amber-50/60 p-2.5">
                    {pending.fields.length > 0 ? (
                      <>
                        <p className="text-[11.5px] font-medium text-amber-800">
                          These values will be replaced:
                        </p>
                        <p className="mt-1 text-[11.5px] text-amber-700">
                          {pending.fields.join(', ')}
                        </p>
                      </>
                    ) : (
                      // Reached when the source image could not be re-read, so
                      // there is no basis for naming fields. Asking without a
                      // list is still better than linking without asking.
                      <p className="text-[11.5px] text-amber-700">
                        Its current metadata could not be checked, so any of these fields may be
                        replaced.
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 border-t border-slate-200 px-4 py-3">
                  <button
                    type="button"
                    disabled={linkMutation.isPending}
                    onClick={dismiss}
                    className="ml-auto rounded-lg px-3 py-1.5 text-[12.5px] font-medium text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={linkMutation.isPending}
                    onClick={() => linkMutation.mutate(pending.ids)}
                    className="rounded-lg bg-[#0014cc] px-3 py-1.5 text-[12.5px] font-medium text-white transition-colors hover:bg-[#0010a3] disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                  >
                    {linkMutation.isPending ? 'Linking…' : 'Replace and link'}
                  </button>
                </div>
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}
